import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

class TimeService {
    sortDates(dateA: string, dateB: string, order: 'asc' | 'desc' = 'desc') {
        if (!dateA || !dateB) {
            return 0;
        }

        const a = dayjs(dateA);
        const b = dayjs(dateB);

        if (a.valueOf() === b.valueOf()) {
            return 0;
        }

        if (order === 'asc') {
            return a.valueOf() < b.valueOf() ? -1 : 1;
        } else {
            return b.valueOf() < a.valueOf() ? -1 : 1;
        }
    }

    isWithinWindow(date: string, releaseWindow: number = 30): boolean {
        const releaseDate = new Date(date);
        const today = new Date();
        const endDate = new Date(releaseDate.getTime() + releaseWindow * 24 * 60 * 60 * 1000);
        return today <= endDate;
    }

    formatDateWithTimezone(
        dateToFormat: string | null | undefined,
        userTimezone: string
    ): string | null {
        if (!dateToFormat) {
            return null;
        }

        return dayjs.tz(dateToFormat, userTimezone).toISOString();
    }

    /**
     * Check if a date string is empty or nil
     */
    private isEmptyDate(dateStr: string | undefined | null): boolean {
        return !dateStr || dateStr === '';
    }

    /**
     * Format a date string to ISO 8601 format with timezone
     * Used for group API calls
     * @param dateStr - The date string to format
     * @param timezone - The timezone to apply
     * @returns Formatted ISO string or undefined if dateStr is empty/nil
     */
    formatDateForGroup(dateStr: string | undefined | null, timezone: string): string | undefined {
        if (this.isEmptyDate(dateStr) || !timezone) return undefined;
        return dayjs.tz(dateStr, timezone).toISOString();
    }

    /**
     * Format a date string to short date format (YYYY-MM-DD)
     * Used for API calls that expect date-only format
     * @param dateStr - The date string to format
     * @returns Formatted date string (YYYY-MM-DD) or undefined if dateStr is empty/nil
     */
    formatDateForAPI(dateStr: string | undefined | null): string | undefined {
        if (this.isEmptyDate(dateStr)) return undefined;
        return dateStr.slice(0, 10);
    }
}

const timeService = new TimeService();

export { timeService as TimeService };
